import React from 'react';
import { AutoTable, Card, CheckboxLabel, DropdownItem, EmptyState, Filter, FilterSetController, Icon, InfoText, Status, TableCellDropdown, WeezHeader } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Event } from '../../../../models';
import LoadingCard from '../../../../components/loadingCard';
import { handleSearchQuery, useCurrentPage, usePageSize, useSort } from '../../../manager/components/context';
import css from '../../../../styles/event/eventFilters.module.css';
import Style from './activationEventList.module.css';

const spec = {
    multiple: ['publication_status__in'],
    fields: ['publication_status__in', 'has_product_for_sale', 'search', 'ordering'],
    form: [],
    aliases: {},
    date: []
};

const defaultFilters = {
    publication_status__in: new Set(['draft', 'published'])
};

export default function ActivationEventList({ organization, productId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const [currentPage, setCurrentPage] = useCurrentPage(0);
    const [filters, setFilters] = React.useState(defaultFilters);
    const [apiOrdering, setApiOrdering] = useSort('start_date');
    const [pageSize] = usePageSize(20);

    const ordering = React.useMemo(
        () => ({
            column: apiOrdering.replace(/^-/, ''),
            direction: apiOrdering.startsWith('-') ? 'DESC' : 'ASC'
        }),
        [apiOrdering]
    );

    const handleSortingChange = ({ column, direction }) => {
        const formattedOrdering = direction === 'DESC' ? `-${column}` : column;
        setApiOrdering(formattedOrdering);
    };

    let handleSearch = React.useCallback(
        (synth, value) => {
            const filterSpread = { ...filters };
            if (value) {
                filterSpread['search'] = value;
            } else {
                delete filterSpread['search'];
            }
            setFilters(filterSpread);
        },
        [filters]
    );

    const [events, loading, , , totalCount, loadingCount] = Event.useApiModel(
        {
            organizationId: organization?.id,
            query: {
                product_id: productId,
                ordering: apiOrdering,
                limit: pageSize,
                offset: currentPage * pageSize,
                ...handleSearchQuery(spec, filters, true)
            }
        },
        [organization, productId, filters, apiOrdering, pageSize, currentPage, spec]
    );

    const options = [
        { key: 'published', text: t('ticket.event.list.statusPublished'), name: 'published' },
        { key: 'draft', text: t('ticket.event.list.statusDraft'), name: 'draft' },
        { key: 'closed', text: t('ticket.event.list.statusClosed'), name: 'closed' }
    ];

    const options_on_sale = [
        { key: 'on_sale', text: t('ticket.cross-selling.activation.listEvent.onSale'), name: 'true' },
        { key: 'unavailable', text: t('ticket.cross-selling.activation.listEvent.unavailable'), name: 'false' }
    ];

    const DateInfo = React.memo(({ date, locale }) => {
        if (!date) {
            return <InfoText>-</InfoText>;
        }

        const d = new Date(date);

        return <InfoText subtitle={Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(d)}>{Intl.DateTimeFormat(locale).format(d)}</InfoText>;
    });

    const renderFilters = React.useMemo(() => {
        return (
            <div className={css['filter-checkboxes']}>
                <span className={css['filter-boxes-label']}>{`${t('ticket.common.action.display')}: `}</span>
                <CheckboxLabel
                    label={t('ticket.cross-selling.activation.listEvent.crossSellingEnabled')}
                    checked={filters.has_product_for_sale}
                    onChange={(e, st) => {
                        setFilters(prev => {
                            if (st.checked) {
                                return { ...prev, has_product_for_sale: true };
                            }

                            const { has_product_for_sale, ...rest } = prev;
                            return { ...rest };
                        });
                    }}
                />
            </div>
        );
    }, [filters]);

    const getStatusLabel = obj => {
        let labelStatus;

        switch (obj.status) {
            case 'draft':
                labelStatus = t('eventList.status.draft');
                break;
            case 'closed':
                labelStatus = t('eventList.status.closed');
                break;
            default:
                labelStatus = t('eventList.status.published');
                break;
        }

        return `E ${obj.id} - ${labelStatus}`;
    };

    return (
        <>
            <WeezHeader title={t('ticket.cross-selling.activation.listEvent.title')}>{t('ticket.cross-selling.activation.listEvent.subtitle')}</WeezHeader>
            <Card>
                <Card.Content>
                    <FilterSetController
                        total={totalCount || 0}
                        isLoadingTotal={loading || loadingCount}
                        labelTotal={t('ticket.cross-selling.activation.listEvent.total', { count: totalCount })}
                        labelReset={t('ticket.common.action.reset')}
                        labelFilters={t('ticket.common.cta.filters')}
                        initialValues={filters}
                        defaultValues={defaultFilters}
                        onChange={values => {
                            if (filters.search !== undefined && filters.search.length > 0) {
                                values.search = filters.search;
                            } else {
                                delete values.search;
                            }
                            setFilters(values);
                        }}
                        search={{
                            onChange: (synth, value) => handleSearch(synth, value),
                            placeholder: t('formEvents.search_label'),
                            value: filters['search']
                        }}
                    >
                        <Filter
                            index="1"
                            multiple
                            labelSelectAll={t('ticket.common.action.select-all')}
                            labelName={t('ticket.accounting.filter.statusLabel')}
                            labelCTA={t('ticket.common.action.apply')}
                            name="publication_status__in"
                            options={options}
                        />
                        <Filter
                            index="2"
                            labelSelectAll={t('ticket.common.action.select-all')}
                            labelName={t('ticket.cross-selling.activation.listEvent.onSaleFilter')}
                            labelCTA={t('ticket.common.action.apply')}
                            name="has_product_for_sale"
                            options={options_on_sale}
                        />
                    </FilterSetController>
                    {renderFilters}
                </Card.Content>
                <LoadingCard loading={loading}>
                    <Card.Content>
                        <AutoTable
                            pk="id"
                            useNew
                            onObjectClicked={obj => history.push(`/ticket/O${organization.id}/products/${productId}/activation/E${obj.id}`)}
                            ordering={ordering}
                            onClickSorting={handleSortingChange}
                            pageSize={+pageSize}
                            onPageChange={numberPage => {
                                setCurrentPage(numberPage);
                            }}
                            totalCount={+totalCount}
                            currentPage={+currentPage}
                            paginator
                            emptyState={
                                <EmptyState
                                    icon={<Icon name="list" />}
                                    title={t('ticket.cross-selling.activation.listEvent.emptyTitle')}
                                    subtitle={t('ticket.cross-selling.activation.listEvent.emptySubtitle')}
                                />
                            }
                            columns={[
                                {
                                    key: 'event',
                                    label: t('ticket.cross-selling.activation.listEvent.event'),
                                    render: obj => (
                                        <InfoText className={Style['name-event-cell-size']} subtitle={getStatusLabel(obj)}>
                                            {obj.name}
                                        </InfoText>
                                    )
                                },
                                {
                                    key: 'start_date',
                                    label: t('ticket.cross-selling.activation.listEvent.startDate'),
                                    sortable: true,
                                    render: obj => <DateInfo date={obj.start_date} locale={organization.default_locale} />
                                },
                                {
                                    key: 'end_date',
                                    label: t('ticket.cross-selling.activation.listEvent.endDate'),
                                    render: obj => <DateInfo date={obj.end_date} locale={organization.default_locale} />
                                },
                                {
                                    key: 'cross_selling_status',
                                    label: t('ticket.cross-selling.activation.listEvent.statusCrossSelling'),
                                    render: obj => (
                                        <Status
                                            text={
                                                obj.has_product_for_sale
                                                    ? t('ticket.cross-selling.activation.listEvent.onSale')
                                                    : t('ticket.cross-selling.activation.listEvent.unavailable')
                                            }
                                            color={obj.has_product_for_sale ? 'green' : 'orange'}
                                        />
                                    )
                                },
                                {
                                    key: 'Actions',
                                    label: t('ticket.cross-selling.listVariant.actions'),
                                    render: obj => (
                                        <TableCellDropdown>
                                            <DropdownItem
                                                item={t('ticket.cross-selling.activation.listEvent.activate')}
                                                onClick={() => {
                                                    history.push(`/ticket/O${organization.id}/products/${productId}/activation/E${obj.id}`);
                                                }}
                                            />
                                        </TableCellDropdown>
                                    )
                                }
                            ]}
                            objects={events}
                        />
                    </Card.Content>
                </LoadingCard>
            </Card>
        </>
    );
}
